import React, { useState, useEffect } from 'react';
import { Form, Modal, notification, Row, Col, Select, SelectProps, InputNumber } from 'antd';
import Button from '/react4xp/mixins/ui/button/button';
import { IPollEditModal, TServiceUrl } from '../express-help.types';
import TextArea from 'antd/es/input/TextArea';
import Checkbox from 'antd/lib/checkbox';
import {
    getQuestionList,
    getResultList,
    updateAnswer,
    updateQuestion,
    updateQuestionnaire
} from "/react4xp/parts/express-help/actions";

const EditQuestionnaireModal = (props: IPollEditModal) => {
    const { selectedType, selectedObject } = props;

    const [isSaving, setSaving] = useState(false);
    const [results, setResults] = useState<SelectProps['options']>([]);
    const [questions, setQuestions] = useState<SelectProps['options']>([]);

    const generateFormFields = (selectedType: TServiceUrl) => {
        let form_field_names = {};

        if (selectedType.service == 'Questionnaire') {
            form_field_names = {
                externalQuestionnaireId: 'ИД',
                questionnaireKk: 'Наименование на казахском языке',
                questionnaireRu: 'Наименование на русском языке',
                questionnaireEn: 'Наименование на английском языке',
                questionnaireWeight: 'Вес',
            };
        } else if (selectedType.service == 'Question') {
            form_field_names = {
                externalQuestionId: 'ИД',
                questionKk: 'Вопрос на казахском языке',
                questionRu: 'Вопрос на русском языке',
                questionEn: 'Вопрос на английском языке',
                questionWeight: 'Вес',
            };
        } else if (selectedType.service == 'Answer') {
            form_field_names = {
                externalAnswerId: 'ИД',
                answerKk: 'Ответ на казахском языке',
                answerRu: 'Ответ на русском языке',
                answerEn: 'Ответ на английском языке',
            };
        }

        const form_field_items = [];

        Object.keys(form_field_names).forEach((field_name) => {
            let isFieldEditable = true;
            let isFieldTextArea = true;
            let isFieldRequired = true;

            if (/^external.*/.test(field_name)) {
                isFieldEditable = false;
                isFieldTextArea = false;
                isFieldRequired = false;
            }

            if (/[a-zA-Z]+weight$/i.test(field_name)) {
                isFieldTextArea = false;
            }

            form_field_items.push(
                <Col span={24}>
                    <Form.Item
                        name={field_name}
                        label={form_field_names[field_name]}
                        rules={[
                            {
                                required: isFieldRequired,
                            },
                        ]}
                    >
                        {isFieldTextArea ? (
                            <TextArea
                                placeholder="Введите"
                                maxLength={1000}
                                disabled={!isFieldEditable}
                            />
                        ) : (
                            <InputNumber
                                placeholder="Введите"
                                max={999}
                                min={0}
                                disabled={!isFieldEditable}
                            />
                        )}
                    </Form.Item>
                </Col>
            );
        });

        if (selectedType.service == 'Answer') {
            // result
            form_field_items.push(
                <>
                    <Col
                        xs={24}
                        md={24}
                    >
                        <Form.Item
                            name={'resultsId'}
                            label={'Результат'}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder={'Выберите'}
                                showArrow={false}
                                filterOption={false}
                                notFoundContent={<p>не найдено</p>}
                                options={(results || []).map((d) => ({
                                    value: d.id,
                                    label: d.resultRu,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                </>
            );

            // next question
            form_field_items.push(
                <>
                    <Col span={18}>
                        <Form.Item
                            name={'nextQuestionId'}
                            label={'Добавочный вопрос'}
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder={'Выберите'}
                                showArrow={false}
                                filterOption={false}
                                notFoundContent={<p>не найдено</p>}
                                options={(questions || []).map((d) => ({
                                    value: d.id,
                                    label: d.questionRu,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                </>
            );
        }

        if (selectedType.service == 'Questionnaire') {
            form_field_items.push(
                <Col span={24}>
                    <Form.Item name='published' valuePropName="checked">
                        <Checkbox>Опубликовать</Checkbox>
                    </Form.Item>
                </Col>
            );
        }

        return <Row gutter={{ xs: 8, md: 24 }}>{form_field_items.map((item) => item)}</Row>;
    };

    const fulfillFormFields = () => {
        if (selectedObject) {
            form.setFieldsValue(selectedObject);
        }

        if (selectedType.service == 'Answer') {
            getResultList().then((response) => {
                setResults(response.data);
            });

            getQuestionList().then((response) => {
                setQuestions(response.data.content);
            });
        }
    };

    const getFormTitle = (selectedType: TServiceUrl) => {
        if (selectedType.service == 'Questionnaire') {
            return 'Редактирование опроса';
        }
        if (selectedType.service == 'Question') {
            return 'Редактирование вопроса';
        }
        if (selectedType.service == 'Answer') {
            return 'Редактирование ответа';
        }
    };

    const [form] = Form.useForm();

    const getUpdateRequest = (id: number, data: any) => {
      switch (selectedType.service) {
          case 'Question': return updateQuestion(id, data);
          case 'Answer': return updateAnswer(id, data);
      }

      return updateQuestionnaire(id, data);
    };

    const onFinish = () => {
        form.validateFields().then(() => {
            if (selectedObject) {
                setSaving(true);

                getUpdateRequest(selectedObject.id, {
                    ...selectedObject,
                    ...form.getFieldsValue()
                })
                    .then(() => {
                        notification['success']({
                            message: 'Данные обновлены!',
                        });
                        props.handleOk();
                        form.resetFields();
                    })
                    .finally(() => setSaving(false));
            }
        });
    };

    const handleCancel = () => {
        props.handleCancel();
        form.resetFields();
    };

    useEffect(() => {
        fulfillFormFields();
    }, [selectedType, selectedObject, form]);

    return (
        <Modal
            title={getFormTitle(selectedType)}
            open={props.isModalOpen}
            onOk={onFinish}
            onCancel={handleCancel}
            footer={[
                <Button onClick={handleCancel}>Отмена</Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={onFinish}
                    loading={isSaving}
                    color="gold"
                >
                    Сохранить
                </Button>,
            ]}
        >
            <div>
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={onFinish}
                >
                    {generateFormFields(selectedType)}
                </Form>
            </div>
        </Modal>
    );
};

export default EditQuestionnaireModal;
